import {minmax, sections, sectionToFixedMap, portrait} from './shared.js';
import {queueTicker} from './ticker.js';

import * as chapter1 from './chapters/chapter1.js';
import * as chapter2 from './chapters/chapter2.js';
import * as chapter3 from './chapters/chapter3.js';
import * as chapter4 from './chapters/chapter4.js';
import * as chapter5 from './chapters/chapter5.js';
import * as chapter6 from './chapters/chapter6.js';
import * as chapter7 from './chapters/chapter7.js';
import * as chapter8 from './chapters/chapter8.js';
import * as chapter9 from './chapters/chapter9.js';
import * as chapter10 from './chapters/chapter10.js';
import * as chapter11 from './chapters/chapter11.js';
import * as chapter13 from './chapters/chapter13.js';
import * as chapter14 from './chapters/chapter14.js';
import * as chapter15 from './chapters/chapter15.js';
import * as chapter16 from './chapters/chapter16.js';
import * as chapter17 from './chapters/chapter17.js';
import * as chapter18 from './chapters/chapter18.js';
import * as chapter19 from './chapters/chapter19.js';

const videoPromiseMap = new Map();

/**
 * Pause a video
 * @param {HTMLVideoElement} video video to pause
 * @return {Promise} resolves when video is paused
 */
function pauseVideo(video) {
  console.log('pause video ' + video.src);
  return Promise.resolve(video.pause());
}

/**
 * Play a video
 * @param {HTMLVideoElement} video video to pause
 * @return {Promise} resolve
 */
function playVideo(video) {
  console.log(`play video ${video.src}`);
  // Edge returns undefined instead of a promise http://developer.microsoft.com/en-us/microsoft-edge/platform/issues/11998448
  return (video.play() || Promise.resolve()).catch(error => console.log(`play aborted: ${video.src}`));
}

/**
 * Adapt videos to changing orientation - switch src to portrait/landscape
 */
function adaptVideos() {
  for (const section of sections) {
    const fixed = sectionToFixedMap.get(section);

    for (const video of fixed.querySelectorAll('video')) {
      if (portrait.matches && video.src !== video.dataset.portrait) {
        video.src = video.classList.contains('only-desktop') ? '' : video.dataset.portrait;
        video.poster = video.classList.contains('only-desktop') ? '' : video.dataset.posterPortrait;
      } else if (!portrait.matches && video.src !== video.dataset.landscape) {
        video.src = video.dataset.landscape;
        video.poster = video.dataset.posterLandscape;
      }
    }
  }
}

/**
 * Initialize all chapters
 */
export function setupChapters() {
  console.log('chapters:setupChapters');

  for (const section of sections) {
    const fixedContent = sectionToFixedMap.get(section);

    if (section.dataset.year) {
      const yearChange = ({detail: {vh}}) => {
        if (vh >= 100) {
          queueTicker(+section.dataset.year);
        }
      };

      section.addEventListener('active', () => section.addEventListener('scrub', yearChange));
      section.addEventListener('inactive', () => section.removeEventListener('scrub', yearChange));
    }

    observer.observe(section);

    if (!fixedContent) {
      continue;
    }

    if (fixedContent.classList.contains('video-fullvid')) {
      const video = fixedContent.querySelector('video');
      video.addEventListener('pause', () => fixedContent.classList.remove('video-playing'));
      video.addEventListener('ended', () => {
        fixedContent.classList.remove('video-playing');
        video.currentTime = 0;
      });
    }

    section.addEventListener('scrub', ({detail: {percent, vh}}) => {
      if (vh < 100 || percent >= 1) {
        if (fixedContent.classList.contains('active')) {
          section.dispatchEvent(new CustomEvent('inactive'));
          fixedContent.classList.remove('active');
        }
      } else {
        if (!fixedContent.classList.contains('active')) {
          section.dispatchEvent(new CustomEvent('active'));
          fixedContent.classList.add('active');
        }
      }

      if (fixedContent.classList.contains('active')) {
        if (section.classList.contains('has-video') && section.classList.contains('video-loop')) {
          const video = fixedContent.querySelector('video');
          if (!portrait.matches || !video.classList.contains('only-desktop')) {
            if (video && video.paused) {
              video.currentTime = 0;
              playVideo(video).then(() => fixedContent.classList.add('video-playing'));
            }
          }
        }
      } else {
        if (section.classList.contains('has-video')) {
          const video = fixedContent.querySelector('video');
          if (!portrait.matches || !video.classList.contains('only-desktop')) {
            if (video && !video.paused) {
              pauseVideo(video).then(() => fixedContent.classList.remove('video-playing'));
            }
          }
        }
      }
    });

    scrubber(section);
  }

  for (const fullvid of document.querySelectorAll('section.video-fullvid')) {
    const fixedContent = sectionToFixedMap.get(fullvid);
    const video = fixedContent.querySelector('video');
    const captions = fixedContent.querySelectorAll('.captions li');

    if (captions.length > 0) {
      setupVideoCaptions(video, captions);
    }
  }

  for (const article of document.querySelectorAll('article')) {
    article.addEventListener('click', () => console.log('article click'));
  }

  document.body.addEventListener('click', () => {
    if (!document.body.classList.contains('intro-done')) {
      return;
    }

    const [fixedContent] = Array.from(sectionToFixedMap.values()).filter(fixedContent => {
      return fixedContent.classList.contains('video-fullvid') && fixedContent.classList.contains('active');
    });

    if (!fixedContent) {
      return;
    }

    const video = fixedContent.querySelector('video');
    if (video.paused) {
      playVideo(video).then(() => fixedContent.classList.add('video-playing'));
    } else {
      pauseVideo(video).then(() => fixedContent.classList.remove('video-playing'));
    }
  });

  chapter1.init();
  chapter2.init();
  chapter3.init();
  chapter4.init();
  chapter5.init();
  chapter6.init();
  chapter7.init();
  chapter8.init();
  chapter9.init();
  chapter10.init();
  chapter11.init();
  chapter13.init();
  chapter14.init();
  chapter15.init();
  chapter16.init();
  chapter17.init();
  chapter18.init();
  chapter19.init();
}

/**
 * Setup video captions for video
 * @param {Node} video the video
 * @param {NodeList} captions the captions for the video
 */
function setupVideoCaptions(video, captions) {
  video.addEventListener('timeupdate', () => {
    for (const caption of captions) {
      if (video.currentTime >= +caption.dataset.start && video.currentTime <= +caption.dataset.end) {
        caption.classList.add('current');
      } else {
        caption.classList.remove('current');
      }
    }
  });

  // set the first caption to current so it shows even before the video starts playing
  if (captions.length > 0) {
    captions[0].classList.add('current');
  }
}

/**
 * Scrub a section based on its scroll position. Called directly and used as a bound event listener.
 * @param {DOMNode} section bound section
 */
function scrubber(section) {
  const {top, bottom} = section.getBoundingClientRect();
  const distanceTopToVPBottom = -(top - window.innerHeight);
  const percent = minmax(0.0, (distanceTopToVPBottom - window.innerHeight) / section.scrollHeight, 1.0);
  const vh = 100 * distanceTopToVPBottom / window.innerHeight;

  section.dispatchEvent(new CustomEvent('scrub', {detail: {percent, vh}}));
}

const sponsorHeader = document.querySelector('#sponsor-content-header');
const sponsorHeaderOffset = sponsorHeader.offsetTop;
const sectionScrubbers = new WeakMap(Array.from(sections).map(section => [section, scrubber.bind(undefined, section)]));

window.addEventListener('scroll', () => {
  if (window.pageYOffset >= sponsorHeaderOffset) {
    sponsorHeader.classList.add('stick');
  } else {
    sponsorHeader.classList.remove('stick');
  }
}, {passive: true});

const sectionVisibilityTimeouts = new Map(Array.from(sections).map(section => [section, null]));

const observer = new IntersectionObserver(entries => {
  for (const entry of entries) {
    const {isIntersecting, target: section} = entry;
    const fixedBg = sectionToFixedMap.get(section);
    const scrubber = sectionScrubbers.get(section);
    const fixedContent = sectionToFixedMap.get(section);

    // start and stop listening to scroll events with the scrubber when the section is or is not intersecting
    if (isIntersecting) {
      section.dispatchEvent(new CustomEvent('intersection'));
      section.classList.add('intersecting');
      if (fixedContent) {
        clearTimeout(sectionVisibilityTimeouts.get(section));
        fixedContent.classList.add('visible');
      }
      window.addEventListener('scroll', scrubber, {passive: true});
      scrubber();
    } else {
      section.dispatchEvent(new CustomEvent('unintersection'));
      section.classList.remove('intersecting');
      if (fixedContent) {
        clearTimeout(sectionVisibilityTimeouts.get(section));
        sectionVisibilityTimeouts.set(section, setTimeout(() => fixedContent.classList.remove('visible'), 500));
      }
      window.removeEventListener('scroll', scrubber);
      scrubber();
    }
  }
}, {threshold: 0});

adaptVideos();

portrait.addListener(() => {
  adaptVideos();
  window.dispatchEvent(new CustomEvent('scroll'));
});

